// =============================================================================
// FlowSense — Road Infrastructure Service Layer
// -----------------------------------------------------------------------------
// Full coverage for /api/v1/roads endpoints (controller v1.0)
//
// 🚀 UPDATE: Complete service layer matching backend roadController.js
//   - All 21 endpoints (3 original + 18 enterprise)
//   - Param sanitization (drops null/undefined/empty strings)
//   - Standardized response envelope handling
//   - CSV blob download with cleanup
//   - Error normalization with user-friendly messages
//   - Added `updateRoadCoordinates` alias for GIS mapping UI.
// =============================================================================

import { authenticatedFetch } from './api';
import { config } from '../config';

const BASE_PATH = '/roads';

// -----------------------------------------------------------------------------
// 🚀 UPDATE: Internal Utilities
// -----------------------------------------------------------------------------

/**
 * Strip null / undefined / empty-string values from a params object,
 * then return a serialized query string (no leading '?').
 */
const cleanQuery = (params = {}) => {
    const cleaned = Object.entries(params).reduce((acc, [k, v]) => {
        if (v !== null && v !== undefined && v !== '') acc[k] = v;
        return acc;
    }, {});
    return new URLSearchParams(cleaned).toString();
};



/**
 * Resolve auth token (matches your existing convention).
 */
const getToken = () =>
    localStorage.getItem('flowSense_token')
    || localStorage.getItem('authToken')
    || localStorage.getItem('token')
    || '';

// =============================================================================
// EXISTING ENDPOINTS (preserved verbatim)
// =============================================================================

export const getRoadAnalyticsOverview = () => {
    return authenticatedFetch(`${BASE_PATH}/analytics/overview`);
};

export const getRoadsAdvanced = (params = {}) => {
    // Filter out null/undefined/empty string values
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            acc[key] = value;
        }
        return acc;
    }, {});

    const queryString = new URLSearchParams(cleanParams).toString();
    return authenticatedFetch(`${BASE_PATH}/list?${queryString}`);
};

export const getRoadLookups = () => {
    return authenticatedFetch(`${BASE_PATH}/lookups`);
};

export const createRoadWorkOrder = (id, payload) => {
    return authenticatedFetch(`${BASE_PATH}/${id}/work-order`, {
        method: 'POST',
        body: JSON.stringify(payload)
    });
};

export const exportRoadsCsv = async () => {
    const token = getToken(); 
    
    // Dynamically uses https://flowsense.live/api/v1
    const response = await fetch(`${config.apiUrl}${BASE_PATH}/export/csv`, {
        headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) throw new Error('Failed to export CSV');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `roads_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

// =============================================================================
// 🚀 UPDATE — NEW ENTERPRISE SERVICE METHODS
// =============================================================================

// --- UPDATE: Legacy / simple list (matches GET /api/v1/roads) ----------------

/**
 * Fetch roads (simple endpoint — for backwards compatibility).
 * GET /api/v1/roads
 */
export const getRoads = (params = {}) => {
    const qs = cleanQuery(params);
    return authenticatedFetch(`${BASE_PATH}${qs ? `?${qs}` : ''}`);
};

// --- UPDATE: Stats endpoint (legacy dashboard) -------------------------------

/**
 * Fetch the simple stats payload (total roads, total km, maintenance needed).
 * GET /api/v1/roads/stats
 */
export const getRoadStats = () => {
    return authenticatedFetch(`${BASE_PATH}/stats`);
};

// --- UPDATE: Single road detail ----------------------------------------------

/**
 * Fetch a single road with recent work orders + open WO count.
 * GET /api/v1/roads/:id
 */
export const getRoadById = (id) => {
    if (!id) return Promise.reject(new Error('Road ID is required'));
    return authenticatedFetch(`${BASE_PATH}/${id}`);
};

// --- UPDATE: CRUD operations -------------------------------------------------

/**
 * Create a new road asset.
 * POST /api/v1/roads
 */
export const createRoad = (payload) => {
    return authenticatedFetch(BASE_PATH, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
};

/**
 * Partial update of a road asset.
 * PATCH /api/v1/roads/:id
 */
export const updateRoad = (id, payload) => {
    if (!id) return Promise.reject(new Error('Road ID is required'));
    return authenticatedFetch(`${BASE_PATH}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
    });
};

// 🚀 FIXED: Added alias specifically for the UI Map Drawing
export const updateRoadCoordinates = updateRoad;

/**
 * Soft-delete a road. Set force=true to override open work order block.
 * DELETE /api/v1/roads/:id
 */
export const deleteRoad = (id, { force = false } = {}) => {
    if (!id) return Promise.reject(new Error('Road ID is required'));
    const qs = force ? '?force=true' : '';
    return authenticatedFetch(`${BASE_PATH}/${id}${qs}`, {
        method: 'DELETE',
    });
};

// --- UPDATE: Geospatial ------------------------------------------------------

/**
 * Find roads within a radius (meters) of given coordinates.
 * GET /api/v1/roads/nearby?lat=&lng=&radius_m=
 */
export const getRoadsNearby = ({ lat, lng, radius_m = 1000 } = {}) => {
    if (lat == null || lng == null) {
        return Promise.reject(new Error('lat & lng are required'));
    }
    const qs = cleanQuery({ lat, lng, radius_m });
    return authenticatedFetch(`${BASE_PATH}/nearby?${qs}`);
};

// --- UPDATE: Bulk operations -------------------------------------------------

/**
 * Bulk update status across multiple roads.
 * POST /api/v1/roads/bulk-status
 */
export const bulkUpdateRoadStatus = (ids, status) => {
    if (!Array.isArray(ids) || ids.length === 0) {
        return Promise.reject(new Error('ids must be a non-empty array'));
    }
    return authenticatedFetch(`${BASE_PATH}/bulk-status`, {
        method: 'POST',
        body: JSON.stringify({ ids, status }),
    });
};

// --- UPDATE: Condition assessment --------------------------------------------

/**
 * Record a condition assessment (PCI-style).
 * POST /api/v1/roads/:id/condition-assessment
 */
export const recordConditionAssessment = (id, payload) => {
    if (!id) return Promise.reject(new Error('Road ID is required'));
    return authenticatedFetch(`${BASE_PATH}/${id}/condition-assessment`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
};

/**
 * Retrieve condition history for a road.
 * GET /api/v1/roads/:id/condition-history
 */
export const getConditionHistory = (id, params = {}) => {
    if (!id) return Promise.reject(new Error('Road ID is required'));
    const qs = cleanQuery(params);
    return authenticatedFetch(`${BASE_PATH}/${id}/condition-history${qs ? `?${qs}` : ''}`);
};

// --- UPDATE: Inspection scheduling -------------------------------------------

/**
 * Schedule an inspection.
 * POST /api/v1/roads/:id/schedule-inspection
 */
export const scheduleInspection = (id, payload) => {
    if (!id) return Promise.reject(new Error('Road ID is required'));
    return authenticatedFetch(`${BASE_PATH}/${id}/schedule-inspection`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
};

/**
 * Get system-wide upcoming inspections.
 * GET /api/v1/roads/inspections/upcoming?days=30
 */
export const getUpcomingInspections = (params = {}) => {
    const qs = cleanQuery({ days: 30, ...params });
    return authenticatedFetch(`${BASE_PATH}/inspections/upcoming?${qs}`);
};

// --- UPDATE: Citizen issue reporting -----------------------------------------

/**
 * Submit a citizen-reported issue against a road.
 * POST /api/v1/roads/:id/report-issue
 */
export const reportRoadIssue = (id, payload) => {
    if (!id) return Promise.reject(new Error('Road ID is required'));
    return authenticatedFetch(`${BASE_PATH}/${id}/report-issue`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
};

/**
 * List issues for a road.
 * GET /api/v1/roads/:id/issues
 */
export const getRoadIssues = (id, params = {}) => {
    if (!id) return Promise.reject(new Error('Road ID is required'));
    const qs = cleanQuery(params);
    return authenticatedFetch(`${BASE_PATH}/${id}/issues${qs ? `?${qs}` : ''}`);
};

// --- UPDATE: Maintenance & cost analytics ------------------------------------

/**
 * Aggregate maintenance history (work orders + spend).
 * GET /api/v1/roads/:id/maintenance-history
 */
export const getMaintenanceHistory = (id) => {
    if (!id) return Promise.reject(new Error('Road ID is required'));
    return authenticatedFetch(`${BASE_PATH}/${id}/maintenance-history`);
};

/**
 * Time-series trend analytics.
 * GET /api/v1/roads/analytics/trends?months=12
 */
export const getRoadTrends = (params = {}) => {
    const qs = cleanQuery({ months: 12, ...params });
    return authenticatedFetch(`${BASE_PATH}/analytics/trends?${qs}`);
};

// --- UPDATE (Drive Mode AI review) -------------------------------------------
/** Drive Mode AI: totals, damage per segment, findings with photo + AI boxes. GET /roads/drive/overview */
export const getDriveOverview = (params = {}) => {
    const qs = cleanQuery(params);
    return authenticatedFetch(`${BASE_PATH}/drive/overview${qs ? `?${qs}` : ''}`);
};
/** Confirm / correct / reject a Drive Mode photo (trains the detection model). */
export const reviewDriveFinding = (findingId, verdict, label) => authenticatedFetch(`${BASE_PATH}/drive/findings/${encodeURIComponent(findingId)}/review`, {
    method: 'POST', body: JSON.stringify({ verdict, label }),
});
/** Confirmed drive damage → segment distress → ML / rules segment PCI. */
export const refreshDriveSegmentHealth = (roadId) => {
    if (!roadId) return Promise.reject(new Error('Road ID is required'));
    return authenticatedFetch(`${BASE_PATH}/${roadId}/segments/drive-health`, { method: 'POST', body: '{}' });
};
/** Road-damage detection model: status, training data, runs. GET /vision/stats */
export const getVisionModelStats = () => authenticatedFetch(`${BASE_PATH}/vision/stats`);
/** Start a detection model training run. POST /vision/train */
export const startVisionModelTraining = () => authenticatedFetch(`${BASE_PATH}/vision/train`, { method: 'POST', body: '{}' });
// --- END UPDATE ------------------------------------------------------------------

// =============================================================================
// 🚀 UPDATE — Default export for namespace-style imports
// =============================================================================

export default {
    // existing
    getRoadAnalyticsOverview,
    getRoadsAdvanced,
    getRoadLookups,
    createRoadWorkOrder,
    exportRoadsCsv,
    // new
    getRoads,
    getRoadStats,
    getRoadById,
    createRoad,
    updateRoad,
    updateRoadCoordinates, // Alias exported
    deleteRoad,
    getRoadsNearby,
    bulkUpdateRoadStatus,
    recordConditionAssessment,
    getConditionHistory,
    scheduleInspection,
    getUpcomingInspections,
    reportRoadIssue,
    getRoadIssues,
    getMaintenanceHistory,
    getRoadTrends,
    // --- UPDATE (Drive Mode AI review)
    getDriveOverview,
    reviewDriveFinding,
    refreshDriveSegmentHealth,
    getVisionModelStats,
    startVisionModelTraining,
    // --- END UPDATE
};